import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { GetErrorMessageByFieldNameType, RemoveErrorType, SetErrorType } from '../hooks/useErrors';
import formatCep from '../utils/formatCep';
import getCepData from '../utils/getCepData';
import isFullCoordValid from '../utils/isFullCoordValid';
import onlyNumbers from '../utils/onlyNumbers';
import { AsideInputsContainer } from './AsideInputsContainer';
import FilterRadioButton from './FilterRadioButtons';
import { FilterRadioButtonsContainer } from './FilterRadioButtonsContainer';
import FormGroup from './FormGroup';
import Input from './Input';
import { SecondaryButton } from './SecondaryButton';

interface IResultsMapsApi {
  address_components: {
    long_name: string;
    short_name: string;
    street_number: string;
    types: string[];
  }[];
}
interface IFullAddressForm {
  lat: number | string;
  lng: number | string;
  cep: string;
  streetName: string;
  number: number | string;
  district: string;
  city: string;
  uf: string;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setError: SetErrorType;
  removeError: RemoveErrorType;
  getErrorMessageByFieldName: GetErrorMessageByFieldNameType;
  onLatChange: (value: string) => void
  onLngChange: (value: string) => void
  onCepChange: (value: string) => void
  onStreetNameChange: (value: string) => void
  onNumberChange: (value: string | number) => void
  onDistrictChange: (value: string) => void
  onCityChange: (value: string) => void
  onUfChange: (value: string) => void
}

export default function FullAddressForm({
  lat,
  lng,
  cep,
  streetName,
  number,
  district,
  city,
  uf,
  onLatChange,
  onLngChange,
  onCepChange,
  onStreetNameChange,
  onNumberChange,
  onDistrictChange,
  onCityChange,
  onUfChange,
  setError,
  setIsLoading,
  removeError,
  getErrorMessageByFieldName,
}: IFullAddressForm) {
  const [addressFillType, setAddressFillType] = useState<'address' | 'coords'>('address');
  const [coords, setCoords] = useState('');
  const [mustFillManualy, setMustFillManualy] = useState(false);

  function handleAddressFillTypeChange(type: 'address' | 'coords') {
    setAddressFillType(type);

    if (type === 'address') {
      onLatChange('');
      onLngChange('');
    } else {
      onCepChange('');
      onStreetNameChange('');
      onNumberChange('');
      onDistrictChange('');
      onCityChange('');
      onUfChange('');
    }
  }

  const handleCepChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      const formattedCep = formatCep(event.target.value);
      onCepChange(formattedCep);

      if (!mustFillManualy) {
        onLatChange('');
        onLngChange('');
      }

      if (!event.target.value) {
        setError({ field: 'cep', message: 'CEP é obrigatório!' });
      }

      if (formattedCep.length === 9 && !mustFillManualy) {
        setIsLoading(true);
        const cepInfo = await getCepData(event.target.value);
        if (cepInfo.erro) {
          setError({ field: 'cep', message: 'CEP inválido!' });
          onCepChange('');
          onStreetNameChange('');
          onDistrictChange('');
          onCityChange('');
          onUfChange('');
          onLatChange('');
          onLngChange('');
          setIsLoading(false);
          toast.error('O CEP não foi encontrado, mas você pode preencher as coordenadas, e o endereço será preenchido automaticamente!');
          return;
        }

        onStreetNameChange(cepInfo.logradouro?.slice(0, 100));
        onDistrictChange(cepInfo.bairro?.slice(0, 25));
        onCityChange(cepInfo.localidade?.slice(0, 30));
        onUfChange(cepInfo.uf);

        setIsLoading(true);
      }
      if (formattedCep.length !== 9 && !mustFillManualy) {
        setError({ field: 'cep', message: 'CEP inválido!' });
        onStreetNameChange('');
        onDistrictChange('');
        onCityChange('');
        onUfChange('');
        onLatChange('');
        onLngChange('');

        setIsLoading(false);
        return;
      }
      removeError('cep');
      setIsLoading(false);
    } catch (error) {
      toast.error(`Ocorreu um erro ao buscar o CEP, mas você pode preencher as coordenadas, e o endereço será preenchido automaticamente (${error})`);
      setIsLoading(false);
    }
  }, [onCepChange, onLatChange, onLngChange, mustFillManualy, removeError, setIsLoading, setError, onStreetNameChange, onDistrictChange, onCityChange, onUfChange]);

  function handleStreetNameChange(event: ChangeEvent<HTMLInputElement>) {
    onStreetNameChange(event.target.value);
    if (!mustFillManualy) {
      onLatChange('');
      onLngChange('');
    }

    if (!event.target.value) {
      setError({ field: 'streetName', message: 'Logradouro é obrigatório' });
    } else {
      removeError('streetName');
    }
  }

  function handleNumberChange(event: ChangeEvent<HTMLInputElement>) {
    const valueAsNumber = onlyNumbers(event.target.value);

    onNumberChange(valueAsNumber);
    if (!mustFillManualy) {
      onLatChange('');
      onLngChange('');
    }

    if (!valueAsNumber) {
      setError({ field: 'number', message: 'Número é obrigatório' });
    } else {
      removeError('number');
    }
  }

  function handleDistrictChange(event: ChangeEvent<HTMLInputElement>) {
    onDistrictChange(event.target.value);

    if (!mustFillManualy) {
      onLatChange('');
      onLngChange('');
    }

    if (!event.target.value) {
      setError({ field: 'district', message: 'Bairro é obrigatório' });
    } else {
      removeError('district');
    }
  }

  function handleCoordinatesChange(event: ChangeEvent<HTMLInputElement>) {
    setCoords(event.target.value);
    console.log('event.target.value');
    console.log(event.target.value);
    onCepChange('');
    onStreetNameChange('');
    onNumberChange('');
    onDistrictChange('');
    onCityChange('');
    onUfChange('');

    if (!isFullCoordValid(event.target.value)) {
      setError({ field: 'coordinates', message: 'Coordenada inválida!' });
      onLatChange('');
      onLngChange('');
    } else {
      const [lati, long] = event.target.value.split(', ');
      console.log('lati, long');
      console.log(lati, long);

      onLatChange(onlyNumbers(lati || '0').toFixed(6));
      onLngChange(onlyNumbers(long || '0').toFixed(6));
      removeError('coordinates');
    }
  }

  const checkAndFindCorrespondingCoords = useCallback(async () => {
    const isMissingSomeInformation = !streetName || !number || !district || !city || !uf || !cep;

    if (isMissingSomeInformation) {
      toast.warn('Por favor, preencha todas as informações do endereço, inclusive o número, para que as coordenadas sejam buscadas');
      return;
    }

    setIsLoading(true);

    const geolocationByAddress = await fetch(
      `${process.env.REACT_APP_NEST_API}/maps-proxy/geocode?address=${streetName} ${number} ${district} ${city} ${uf} ${cep}`,
    );
    const responseJson = await geolocationByAddress.json();

    const geometryLocation = responseJson.results[0]?.geometry.location ?? {
      lat: 0,
      lng: 0,
    };

    setIsLoading(false);

    if (!geometryLocation.lat || !geometryLocation.lng) {
      toast.error('Não foi possível encontrar nenhuma coordenada com base neste endereço. Por favor, mude o endereço ou tente adicioná-lo através da coordenada');
      return;
    }

    onLatChange((geometryLocation.lat).toFixed(6));
    onLngChange((geometryLocation.lng).toFixed(6));
    setCoords(`${(geometryLocation.lat).toFixed(6)}, ${(geometryLocation.lng).toFixed(6)}`);
  }, [cep, city, district, number, setIsLoading, onLatChange, onLngChange, streetName, uf]);

  const checkAndFindCorrespondingAddress = useCallback(async () => {
    if (!isFullCoordValid(`${lat}, ${lng}`)) {
      toast.error('A coordenada precisa estar formatada corretamente para o endereço correspondente ser buscado');
      return;
    }

    setIsLoading(true);

    const geolocationByAddress = await fetch(
      `${process.env.REACT_APP_NEST_API}/maps-proxy/reverse-geocode?latlng=${lat},${lng}`,
    );
    const responseJson = await geolocationByAddress.json();

    setIsLoading(false);

    const results: IResultsMapsApi[] = responseJson.results;

    function findInfoAtAddressComponent(paramName: 'long_name' | 'street_number' | 'short_name', validTypes: string[]): string {
      const desiredComponent = results.find((x) => {
        return x.address_components.find((y: { types: string[]; }) => y.types.some((z: string) => validTypes.includes(z)));
      });

      if (!desiredComponent) return '';
      return desiredComponent.address_components.find((x) => x.types.some((z) => validTypes.includes(z)))?.[paramName] || '';
    }

    const numberApi = findInfoAtAddressComponent('street_number', ['street_number']) || 0;
    const streetNameApi = findInfoAtAddressComponent('long_name', ['route', 'street_address', 'intersection']) || 'Rua Sem Nome';
    const districtApi = findInfoAtAddressComponent('long_name', ['sublocality']);
    const cityApi = findInfoAtAddressComponent('long_name', ['administrative_area_level_2']);
    const ufApi = findInfoAtAddressComponent('short_name', ['administrative_area_level_1']);
    const cepApi = findInfoAtAddressComponent('long_name', ['postal_code']);

    const hasSomeInfoNotFound = !numberApi || streetNameApi === 'Rua Sem Nome' || !districtApi || !cityApi || !ufApi || !cepApi;
    if (hasSomeInfoNotFound) {
      toast.warn('Uma ou mais informações não foram encontradas. Por favor, preencha-as manualmente');
    }

    onStreetNameChange(streetNameApi.slice(0, 100));
    if (streetNameApi) {
      removeError('streetName');
    }

    onDistrictChange(districtApi.slice(0, 25));
    if (districtApi) {
      removeError('district');
    }

    onCityChange(cityApi.slice(0, 30));
    onUfChange(ufApi);

    onCepChange(cepApi);
    if (cepApi) {
      removeError('cep');
    }

    onNumberChange(Number(numberApi));
    if (numberApi) {
      removeError('number');
    }

    if (!cepApi || !streetNameApi || !numberApi || !districtApi) {
      setMustFillManualy(true);
    }
  }, [lat, lng, setIsLoading, onStreetNameChange, onDistrictChange, onCityChange, onUfChange, onCepChange, onNumberChange, removeError]);

  const canFindCorrespondingCoord = useMemo(() => (
    cep && streetName && number && district
  ), [cep, district, number, streetName]);

  const canFindCorrespondingAddress = useMemo(() => (
    lat && lng && isFullCoordValid(`${lat}, ${lng}`)
  ), [lat, lng]);

  return (
    <>
      <FilterRadioButtonsContainer>
        <FilterRadioButton selected={addressFillType === 'address'} onClick={() => handleAddressFillTypeChange('address')}>
                Informar Endereço
        </FilterRadioButton>
        <FilterRadioButton selected={addressFillType === 'coords'} onClick={() => handleAddressFillTypeChange('coords')}>
                Informar Coordenadas
        </FilterRadioButton>
      </FilterRadioButtonsContainer>

      <FormGroup error={getErrorMessageByFieldName('cep')}>
        <label htmlFor="contactName">CEP</label>
        <Input
          placeholder="XXXXX-XXX"
          value={cep}
          onChange={handleCepChange}
          autoComplete="new-password"
          darkBorder
          error={getErrorMessageByFieldName('cep')}
          disabled={(addressFillType === 'coords' && !mustFillManualy)}
          maxLength={9}
        />
      </FormGroup>

      <AsideInputsContainer>
        <FormGroup error={getErrorMessageByFieldName('streetName')}>
          <label htmlFor="contactName">Logradouro</label>
          <Input
            placeholder="Rua, Av., Al., ..."
            value={streetName}
            onChange={handleStreetNameChange}
            autoComplete="new-password"
            darkBorder
            error={getErrorMessageByFieldName('streetName')}
            disabled={(addressFillType === 'coords' && !mustFillManualy)}
            maxLength={100}
          />
        </FormGroup>

        <FormGroup error={getErrorMessageByFieldName('number')}>
          <label htmlFor="contactName">Número</label>
          <Input
            placeholder="Número"
            value={number}
            onChange={handleNumberChange}
            autoComplete="new-password"
            darkBorder
            error={getErrorMessageByFieldName('number')}
            // disabled={(addressFillType === 'coords' && !!number)}
          />
        </FormGroup>
      </AsideInputsContainer>

      <FormGroup error={getErrorMessageByFieldName('district')}>
        <label htmlFor="contactName">Bairro</label>
        <Input
          placeholder="Bairro"
          value={district}
          onChange={handleDistrictChange}
          autoComplete="new-password"
          darkBorder
          error={getErrorMessageByFieldName('district')}
          disabled={(addressFillType === 'coords' && !mustFillManualy)}
          maxLength={25}
        />
      </FormGroup>

      {city && uf && (
        <small>{city} - {uf}</small>
      )}

      {addressFillType === 'address' && (
        <SecondaryButton disabled={!canFindCorrespondingCoord} style={{ margin: '8px 0'}} onClick={() => checkAndFindCorrespondingCoords()}>
          Buscar Coordenada Correspondente
        </SecondaryButton>
      )}

      <FormGroup error={getErrorMessageByFieldName('coordinates')}>
        <label htmlFor="contactName">Coordenadas</label>
        <Input
          placeholder="-XX.XXXXXX, -XX.XXXXXX"
          value={coords || (lat && lng ? `${lat}, ${lng}` : '')}
          onChange={handleCoordinatesChange}
          autoComplete="new-password"
          darkBorder
          error={getErrorMessageByFieldName('coordinates')}
          disabled={addressFillType === 'address'}
        />
      </FormGroup>

      {addressFillType === 'coords' && (
        <SecondaryButton disabled={!canFindCorrespondingAddress} style={{ margin: '8px 0'}} onClick={() => checkAndFindCorrespondingAddress()}>
          Buscar Endereço Correspondente
        </SecondaryButton>
      )}
    </>
  );
}
